import { useActiveEntityFieldsStore } from "../stores/useActiveEntityFieldsStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useOutlineEntitiesStore } from "../stores/useOutlineEntitiesStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";

type Session = ReturnType<typeof useEditorSessionStore>;
type OutlineEntities = ReturnType<typeof useOutlineEntitiesStore>;

/**
 * Entity graph island port (#239): everything `EntityGraphCanvas.vue` reads
 * from or asks of the shell. The canvas owns its own DOM, layout and drag
 * state; it talks to the rest of the app only through this object (plus the
 * `lib/tauri/database` effect helpers for entry fields).
 *
 * Reads are live getters over Pinia, so they stay reactive inside `computed`.
 */
export type EntityGraphPort = {
  readonly rootPath: Session["rootPath"];
  readonly chapters: Session["chapters"];
  readonly projectConfig: Session["projectConfig"];
  readonly activeDatabase: Session["activeDatabase"];
  readonly databaseEntryFields: OutlineEntities["databaseEntryFields"];
  /** Leave graph view for the list/editor view of the Database section. */
  closeGraphView(): void;
  /** Open a manuscript scene (`Chapters/<folder>/<file>`). */
  selectProjectFile(path: string): Promise<void>;
  /** Open a Database entry by file name. */
  selectProjectDatabase(name: string): Promise<void>;
  /** Re-read Database entries after the graph changed links. */
  refreshDatabase(): Promise<void>;
  /** Re-read the active entry's fields after the graph edited them. */
  reloadActiveEntryFields(): Promise<void>;
};

export function useEntityGraphPort(): EntityGraphPort {
  const session = useEditorSessionStore();
  const outlineEntities = useOutlineEntitiesStore();
  const activeEntityFields = useActiveEntityFieldsStore();
  const workspace = useProjectWorkspaceStore();
  return {
    get rootPath() {
      return session.rootPath;
    },
    get chapters() {
      return session.chapters;
    },
    get projectConfig() {
      return session.projectConfig;
    },
    get activeDatabase() {
      return session.activeDatabase;
    },
    get databaseEntryFields() {
      return outlineEntities.databaseEntryFields;
    },
    closeGraphView: () => {
      session.databaseGraphView = false;
    },
    selectProjectFile: (path) => workspace.onSelectProjectFile(path),
    selectProjectDatabase: (name) => workspace.onSelectProjectDatabase(name),
    refreshDatabase: () => outlineEntities.refreshDatabase(),
    reloadActiveEntryFields: () => activeEntityFields.loadFields(),
  };
}
